/**
 * The grounds this device has scored at, for the venue field's autocomplete.
 *
 * Local, not a server list, and that is the whole design. The API carries no venue on a
 * match — `CreateMatchRequest` has no field for one, and a `Fixture`'s venue belongs to a
 * tournament (#44) — so there is nothing to fetch and nothing to send. What a scorer
 * actually needs is that the third Saturday at the same ground costs no typing, and a list
 * of the last few names on this phone answers that completely.
 *
 * Most-recent-first and deduped case-insensitively, because "Shivaji Park" and "shivaji
 * park" are one ground, and an autocomplete that offers both is worse than one that offers
 * neither — it invites the scorer to create the second spelling.
 */
import type { Store } from "../localStore.ts";

const KEY = "sportsos.venues";
/** Enough to cover a season's regular grounds; short enough that the list stays scannable. */
const KEEP = 8;

/** The remembered grounds, newest first. Never throws: a corrupt value reads as none. */
export function recentVenues(store: Store): readonly string[] {
  try {
    const raw = store.getItem(KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === "string" && value !== "");
  } catch {
    // A convenience over a field that already works. Losing it is not worth a broken screen.
    return [];
  }
}

/** Record a ground as used. A blank name records nothing, rather than an empty entry. */
export function rememberVenue(store: Store, venue: string): void {
  const name = venue.trim();
  if (name === "") return;
  const kept = [name, ...recentVenues(store).filter((held) => !sameVenue(held, name))].slice(0, KEEP);
  try {
    store.setItem(KEY, JSON.stringify(kept));
  } catch {
    // Storage full, or a browser refusing writes. The match is unaffected.
  }
}

const sameVenue = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The grounds worth offering under what is being typed.
 *
 * A substring match rather than a prefix one: grounds are remembered as they were typed —
 * "Oval Maidan, Fort" — and a scorer who starts with "fort" means that one. An exact match
 * is filtered out, because a suggestion identical to the field's contents is a tap that
 * does nothing.
 */
export function suggestVenues(
  known: readonly string[],
  typed: string,
  limit = 5,
): readonly string[] {
  const q = typed.trim().toLowerCase();
  const matches = q === "" ? known : known.filter((venue) => venue.toLowerCase().includes(q));
  return matches.filter((venue) => venue.trim().toLowerCase() !== q).slice(0, limit);
}
